import { forwardRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseSelectProps } from '~/components/inputs/Select/Select';
import SimpleSelect from '~/components/inputs/Select/SimpleSelect';
import { useShowPlus15PaymentTerms } from '~/services/company-settings';
import { FlowPaymentTerms } from '~/types/flow';

interface Props extends BaseSelectProps {
  onChange: (value: FlowPaymentTerms | null) => void;
  value: FlowPaymentTerms | null;
  /**
   * Whether to offer the EOM+15 options. Defaults to the saved company setting; forms that
   * edit the setting alongside the value pass their live state so the options react before save.
   */
  plus15Enabled?: boolean;
}

const DefaultPaymentDateSelect: React.FC<Props> = forwardRef(
  ({ onChange, plus15Enabled: plus15EnabledProp, ...props }: Props, ref?: React.Ref<HTMLDivElement>) => {
    const { t } = useTranslation('documents', { keyPrefix: 'builder' });
    const savedPlus15Enabled = useShowPlus15PaymentTerms();
    const plus15Enabled = plus15EnabledProp ?? savedPlus15Enabled;

    const options = useMemo(() => {
      // The "+15" terms only appear when the setting is on, but a value already saved on the
      // entity is always kept so it never silently disappears from the select.
      const showPlus15 = (term: FlowPaymentTerms) => plus15Enabled || props.value === term;

      return [
        {
          value: '',
          label: t('fields.flow_date.delay_0'),
        },
        {
          value: FlowPaymentTerms.EOM,
          label: t('fields.flow_date.delay_eom'),
        },
        {
          value: FlowPaymentTerms.Plus15,
          label: t('fields.flow_date.delay_15'),
        },
        {
          value: FlowPaymentTerms.Plus30,
          label: t('fields.flow_date.delay_30'),
        },
        {
          value: FlowPaymentTerms.Plus60,
          label: t('fields.flow_date.delay_60'),
        },
        {
          value: FlowPaymentTerms.Plus90,
          label: t('fields.flow_date.delay_90'),
        },
        {
          value: FlowPaymentTerms.Plus120,
          label: t('fields.flow_date.delay_120'),
        },
        {
          value: FlowPaymentTerms.Plus150,
          label: t('fields.flow_date.delay_150'),
        },
        {
          value: FlowPaymentTerms.Plus30EOM,
          label: t('fields.flow_date.delay_30eom'),
        },
        ...(showPlus15(FlowPaymentTerms.Plus30EOMPlus15)
          ? [{ value: FlowPaymentTerms.Plus30EOMPlus15, label: t('fields.flow_date.delay_30eomplus15') }]
          : []),
        {
          value: FlowPaymentTerms.Plus60EOM,
          label: t('fields.flow_date.delay_60eom'),
        },
        ...(showPlus15(FlowPaymentTerms.Plus60EOMPlus15)
          ? [{ value: FlowPaymentTerms.Plus60EOMPlus15, label: t('fields.flow_date.delay_60eomplus15') }]
          : []),
        {
          value: FlowPaymentTerms.Plus90EOM,
          label: t('fields.flow_date.delay_90eom'),
        },
        ...(showPlus15(FlowPaymentTerms.Plus90EOMPlus15)
          ? [{ value: FlowPaymentTerms.Plus90EOMPlus15, label: t('fields.flow_date.delay_90eomplus15') }]
          : []),
        {
          value: FlowPaymentTerms.Plus120EOM,
          label: t('fields.flow_date.delay_120eom'),
        },
        ...(showPlus15(FlowPaymentTerms.Plus120EOMPlus15)
          ? [{ value: FlowPaymentTerms.Plus120EOMPlus15, label: t('fields.flow_date.delay_120eomplus15') }]
          : []),
        {
          value: FlowPaymentTerms.Plus150EOM,
          label: t('fields.flow_date.delay_150eom'),
        },
      ];
    }, [t, plus15Enabled, props.value]);

    const handleChange = useCallback((value: string | null) => onChange(value as FlowPaymentTerms | null), [onChange]);
    return (
      <SimpleSelect
        {...props}
        ref={ref}
        emptyValue=""
        onChange={handleChange}
        options={options}
        value={props.value ?? ''}
      />
    );
  },
);

export default DefaultPaymentDateSelect;
